/* eslint-disable react/jsx-indent */
/* eslint-disable max-lines */
import {
  AnnotatedGraph,
  AnnotatedSuperNodeT,
  HighlightedSentence,
  remapNodesHighlighted,
} from '@litlingo/react';
import React, { useCallback, useRef } from 'react';
import { getUser } from 'selectors/auth';
import { getEnvelopeToPreview } from 'selectors/envelopeListView';
import { getModelTransparencyMode } from 'selectors/envelopeReview';
import { getPermissionsPolicy } from 'selectors/permissions';
import { getUserShowHighlights } from 'selectors/users';
import { useSelector } from 'store';
import { getFieldAuthorization } from 'utils/permissions';
import CommentButton from './CommentButton';
import { HoverData } from './WrapperHighlightedSentence';

type ComponentProps = {
  borderSeparation?: string;
  hideHighlight?: boolean;
  handleSentenceClick?: (line: AnnotatedGraph, nodes: number[]) => void;
  index: number;
  line: AnnotatedGraph;
  ruleId: string;
  campaignId: string;
  communicationId: string;
  hoverData: HoverData;
  setHoverData: React.Dispatch<React.SetStateAction<HoverData>>;
};

const HighlightedParagraph: React.FC<ComponentProps> = ({
  borderSeparation,
  hideHighlight = false,
  handleSentenceClick,
  index,
  line,
  ruleId,
  campaignId,
  communicationId,
  hoverData,
  setHoverData,
}) => {
  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const entityFieldLevel = getFieldAuthorization('communications.entity', user.roles, policy);

  const userMetadata = useSelector(getUserShowHighlights);

  const envelopeToPreview = useSelector(getEnvelopeToPreview);
  const modelTransparencyMode = useSelector(getModelTransparencyMode);
  const containerRef = useRef<HTMLDivElement>(null);

  const remapedNodes = remapNodesHighlighted(line.nodes);

  const getSentences = useCallback((): AnnotatedSuperNodeT[][] => {
    const sentences = remapedNodes.reduce<AnnotatedSuperNodeT[][]>((acc, cur) => {
      const curIndex = cur.nodes[0].index;
      if (curIndex === 0) {
        return [...acc, [cur]];
      }
      acc[acc.length - 1].push(cur);
      return acc;
    }, []);
    return sentences;
  }, [remapedNodes]);

  const handleBlockHighlight = (): boolean => {
    if (userMetadata === null) {
      return true;
    }

    if (
      userMetadata.data.showHighlights === undefined ||
      userMetadata.data.showHighlights === null
    ) {
      return true;
    }

    return userMetadata.data.showHighlights;
  };

  const handleClick = (node: number): void => {
    const matchSentences = getSentences().filter((s) =>
      s.some((n) => n.annotations && n.annotations.length > 0)
    );
    const nodeSentence = matchSentences.find(
      (s) => s[0].index <= node && node <= s[s.length - 1].index
    );

    if (handleSentenceClick) {
      if (nodeSentence) {
        handleSentenceClick(line, [
          nodeSentence[0].index,
          nodeSentence[nodeSentence.length - 1].index,
        ]);
      }
    }
  };

  const renderCommentButton = (): JSX.Element[] => {
    const highlightGroups = getSentences();

    return highlightGroups.map((group) => {
      if (group.length === 0) {
        return <></>;
      }

      return (
        <CommentButton
          key={`${index}-${group[0].index}`}
          index={index}
          line={line}
          communicationId={communicationId}
          hoverData={hoverData}
          group={group}
          containerRef={containerRef}
        />
      );
    });
  };

  return (
    <div
      key={line.index}
      ref={containerRef}
      className={`relative pr-5 ${index !== 0 ? 'mt-2' : ''} ${
        handleBlockHighlight() && !envelopeToPreview ? '-ml-1' : '-ml-1'
      }`}
      id={`communication-${communicationId}-line-${line.index}`}
      data-communication-id={communicationId}
      data-rule-id={ruleId}
      data-campaign-id={campaignId}
      data-index={index}
    >
      <HighlightedSentence
        nodes={line.nodes}
        useHover={user.roles.includes('super-admin')}
        protectedFields={{
          entity: entityFieldLevel,
        }}
        portal="tooltip-portal"
        ruleId={ruleId || ''}
        blockHighlight={handleBlockHighlight()}
        borderSeparation={borderSeparation}
        hideHighlight={hideHighlight}
        highlightSentenceOnClickHandle={(node): void => handleClick(node)}
        modelTransparencyMode={modelTransparencyMode}
        setHoverData={(data: HoverData): void => {
          setHoverData({ ...data, line: line.index });
        }}
      />
      {renderCommentButton()}
    </div>
  );
};

export default HighlightedParagraph;
